import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Button, Typography, Box, Grid, LinearProgress } from "@mui/material";
import classes from "./Article.module.css";
import { retrieveArticle } from "../services/article_retriever";
import { aiRequestHandler } from "../services/backend";
import rehypeRaw from 'rehype-raw';

const Article = ({
  title,
  source,
  publishedAt,
  url,
  onAdd,
  setSelectedWord,
  user_id,
  words,
  level,
  language
}) => {
  const [error, setError] = useState(null);

  const [originalText, setOriginalText] = useState(null);
  const [isOriginalTextRetrieving, setIsOriginalTextRetrieving] =
    useState(false);
  const [isOriginalTextRetrieved, setIsOriginalTextRetrieved] = useState(false);

  const [modifiedText, setModifiedText] = useState(null);
  const [isModifiedTextRetrieving, setIsModifiedTextRetrieving] =
    useState(false);
  const [isModifiedTextRetrieved, setIsModifiedTextRetrieved] = useState(false);

  const [highlightedWords, setHighlightedWords] = useState([]);
  const [newHighlightedWords, setNewHighlightedWords] = useState([]);

  const onOriginalTextRetrieved = (content) => {
    console.log("Original text retrieved");
    setOriginalText(content);
    setIsOriginalTextRetrieved(true);
    rewriteArticleHandler(content);
  };

  const onModifiedTextRetrieved = (content) => {
    const article = content.adapted_article;
    const words = content.used_words;
    setHighlightedWords(words);
    setModifiedText(article);
    setIsModifiedTextRetrieved(true);
  };

  const rewriteArticleHandler = async (text) => {
    console.log(`Rewriting with ${words.length} words (${words.slice(0,5).map(w => w.name)})...`);
    aiRequestHandler(
      words.map((w) => w.name),
      text,
      level,
      language,
      user_id,
      setIsModifiedTextRetrieving,
      onModifiedTextRetrieved
    );
  };

  const handleWordClick = (word) => {
    const cleanedWord = processWord(word);
    console.log(cleanedWord);
    onAdd(cleanedWord, user_id);
    setSelectedWord(cleanedWord);
    setNewHighlightedWords([...newHighlightedWords, cleanedWord]);
  };

  const processWord = (word) => {
    return word
      .toLowerCase()
      .trim()
      .replace(/[.!?",;]/gi, "");
  };

  const retrieveArticleHandler = (url) => {
    retrieveArticle(
      url,
      onOriginalTextRetrieved,
      setIsOriginalTextRetrieving,
      setError
    );
  };
  

  const renderGetArticleButton =
    !isOriginalTextRetrieving &&
    !isModifiedTextRetrieving &&
    url &&
    !isOriginalTextRetrieved &&
    !isModifiedTextRetrieved;

  const renderGoToSourceButton =
    isOriginalTextRetrieved && isModifiedTextRetrieved;

  const goToSourceWebsite = (
    <Button onClick={() => window.open(url, "_blank")}>
      Go to source website
    </Button>
  );

  const readArticle = (
    <Button onClick={() => retrieveArticleHandler(url)}>Read article</Button>
  );

  const articleCTA = (
    <>
      {!url && <p>Article unavailable (missing URL)</p>}
      {renderGoToSourceButton && goToSourceWebsite}
      {renderGetArticleButton && readArticle}
    </>
  );

  const clicked = () => {
    try {
      var s = window.getSelection();
      var range = s.getRangeAt(0);
      var node = s.anchorNode;

      const parent = s.baseNode.parentNode.nodeName;
      if (parent == "STRONG") {
        console.log("Shortcut version");
        handleWordClick(s.baseNode.data);
        return;
      }

      // Find starting point
      while (range.toString().indexOf(" ") != 0 && range.startOffset > 0) {
        range.setStart(node, range.startOffset - 1);
      }
      if (
        range.startOffset != 0 ||
        range.toString().indexOf(" ") == range.startOffset
      )
        range.setStart(node, range.startOffset + 1);

      // Find ending point
      do {
        range.setEnd(node, range.endOffset + 1);
      } while (
        range.toString().indexOf(" ") == -1 &&
        range.toString().trim() != ""
      );
    } catch (exceptions) {
      console.log("Can't process this highlight");
      console.log(exceptions);
    }
    var str = range.toString().trim();
    console.log(str);
    handleWordClick(str);
  };

  const formattedDate =
    publishedAt &&
    new Date(publishedAt).toLocaleDateString("ch-CH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const loadingIndicator = (() => {
    if (isOriginalTextRetrieving) {
      return (
        <div>
          <LinearProgress color="success" />
          Retrieving original article...
        </div>
      );
    } else if (isModifiedTextRetrieving) {
      return (
        <div>
          <LinearProgress color="warning" />
          Rewriting article using your level and learning goals...
        </div>
      );
    } else {
      return <></>;
    }
  })();


  const renderTextWithHighlights = (text, primaryHighlights, secondaryHighlights, newHighlightedWords) => {
    const highlights = [...primaryHighlights, ...secondaryHighlights, ...newHighlightedWords];
    if (highlights.length == 0)
      return text;
    
    const regex = new RegExp(`\\b(${highlights.join('|')})\\b`, 'gi');
    const parts = text.split(regex);

    const highlightedParts = parts.map((part, index) => {
      if (primaryHighlights.includes(part.toLowerCase())) {
        return `<span class="${classes.highlighted}">${part}</span>`;
      } else if (secondaryHighlights.includes(part.toLowerCase())) {
        return `<span class="${classes.secondaryHighlighted}">${part}</span>`;
      } else if (newHighlightedWords.includes(part.toLowerCase())) {
        return `<span class="${classes.newHighlighted}">${part}</span>`;
      } else {
        return part;
      }
    });

    return highlightedParts.join('');
  };

  
  var primaryHighlights = [];
  var secondaryHighlights = [];
  var textToRender = null;

  if (isOriginalTextRetrieved && isModifiedTextRetrieved && highlightedWords.length > 0) {
    primaryHighlights = highlightedWords.map((w) => w.original_word);
    secondaryHighlights = highlightedWords.map((w) => w.derived_words_used_in_adapted_article).flat();
    textToRender = (renderTextWithHighlights(modifiedText, primaryHighlights, secondaryHighlights, newHighlightedWords) );

  }

  return (
    <div>
      <p>{error}</p>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={6} order={{ xs: 2, sm: 1 }}>
          <Typography align="left" variant="overline" display="block">
            {source}
          </Typography>
        </Grid>

        <Grid item xs={6} sm={6} order={{ xs: 3, sm: 3 }}>
          <Typography align="right" variant="overline" display="block">
            {formattedDate}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>

      <div className={classes.text}>
        {loadingIndicator}
        {isOriginalTextRetrieved && (
          <div onClick={clicked}>
         <ReactMarkdown rehypePlugins={[rehypeRaw]}>{textToRender}</ReactMarkdown>
          </div>
        )}
        {articleCTA}
      </div>
    </div>
  );
};

export default Article;
